import { JSX } from 'react';
import { DrivingTimesDisplayItem } from '../../tables/drivingtimes/types';
import { ClockCounterComponent } from '../../tables/elements/ClockCounterComponent';
import { DrivingTimesCell } from './elements/DrivingTimesCell';
import {
    MAX_NUMBER_OF_DAILY_REST_VIOLATIONS,
    MAX_NUMBER_OF_RESTING_EXCEED_PER_WEEK,
    MAXIMUM_CONTINUOUS_DRIVING_MINUTES,
    MAXIMUM_DAILY_DRIVING_TIME_EXTENDED_MINUTES,
    MAXIMUM_DAILY_DRIVING_TIME_MINUTES,
    MAXIMUM_TWO_WEEKLY_DRIVING_MINUTES,
    MAXIMUM_WEEKLY_DRIVING_MINUTES,
} from '../../legalDrivingRestrictions';
import { Activities, ActivityConfig } from '../../enums/Activities';
import { FormattedMessage } from 'react-intl';
import { LiveClock } from '../../realtime/header/LiveClock';
import { TableColumn } from '../../enums/TableColumn';
import { RestingTimesCell } from './elements/RestingTimesCell';

interface DrivingTimesProps {
    displayItem: DrivingTimesDisplayItem;
}

const DrivingTimesHeaderComponent = (props: DrivingTimesProps): JSX.Element => (
    <div className={'display-flex justify-content-between padding-bottom-5'} data-testid={'driving-times-header'}>
        <div>
            <span className={'rioglyph rioglyph-status-driving  text-color-status-driving padding-right-3'} />
            <span className={'rioglyph rioglyph-status-resting text-color-status-resting padding-right-3'} />
            <FormattedMessage id={'intl-msg:timed.common.drivingTimes.label'} />
        </div>
        <div>
            <LiveClock date={props.displayItem[TableColumn.DRIVING_TIMES_TIMESTAMP]} />
        </div>
    </div>
);

enum DrivingtimeViewCells {
    CONTINOUS_DRIVING,
    DAILY_DRIVING,
    WEEKLY_DRIVING,
    TWO_WEEKLY_DRIVING,
    DAILY_REST,
    WEEKLY_REST,
}

const getTestId = (cell: DrivingtimeViewCells): string => {
    switch (cell) {
        case DrivingtimeViewCells.CONTINOUS_DRIVING:
            return 'continuous-driving-time';
        case DrivingtimeViewCells.DAILY_DRIVING:
            return 'daily-driving-time';
        case DrivingtimeViewCells.WEEKLY_DRIVING:
            return 'weekly-driving-time';
        case DrivingtimeViewCells.TWO_WEEKLY_DRIVING:
            return 'two-weekly-driving-time';
        case DrivingtimeViewCells.DAILY_REST:
            return 'daily-rest-time';
        case DrivingtimeViewCells.WEEKLY_REST:
            return 'weekly-rest-time';
    }
};

export const DrivingTimesView = (props: DrivingTimesProps): JSX.Element | null => {
    const { displayItem } = props;

    if (!displayItem) {
        return null;
    }

    return (
        <div className={'margin-bottom-20'}>
            <DrivingTimesHeaderComponent displayItem={displayItem} />
            <div className={'display-grid grid-cols-2 gap-20'}>
                <div
                    className={'display-flex flex-column border rounded'}
                    data-testid={getTestId(DrivingtimeViewCells.CONTINOUS_DRIVING)}
                >
                    <DrivingTimesCell
                        testIdPrefix={getTestId(DrivingtimeViewCells.CONTINOUS_DRIVING)}
                        currentMinutes={displayItem[TableColumn.CONTINUOUS_DRIVING]?.duration}
                        remainingMinutes={displayItem[TableColumn.REMAINING_CURRENT_DRIVING_TIME]?.duration}
                        maximumMinutes={MAXIMUM_CONTINUOUS_DRIVING_MINUTES}
                        headerMessageId={'intl-msg:timed.common.signalHeader.continuousDrivingTime'}
                        textColor={'status-driving'}
                        progressBarColor={'progress-bar-status-driving'}
                    />
                </div>
                <div
                    className={'display-flex flex-column border rounded'}
                    data-testid={getTestId(DrivingtimeViewCells.DAILY_DRIVING)}
                >
                    <DrivingTimesCell
                        testIdPrefix={getTestId(DrivingtimeViewCells.DAILY_DRIVING)}
                        currentMinutes={displayItem[TableColumn.DRIVING_TODAY_CURRENT]?.duration}
                        remainingMinutes={displayItem[TableColumn.REMAINING_DRIVING_TODAY_CURRENT]?.duration}
                        maximumMinutes={MAXIMUM_DAILY_DRIVING_TIME_EXTENDED_MINUTES}
                        maximumWithoutExtensionMinutes={MAXIMUM_DAILY_DRIVING_TIME_MINUTES}
                        headerMessageId={'intl-msg:timed.common.signalHeader.day'}
                        textColor={'status-driving'}
                        progressBarColor={'progress-bar-status-driving'}
                    >
                        <span className={'margin-right-5'} />
                        <ClockCounterComponent
                            counter={displayItem[TableColumn.REMAINING_DRIVING_TODAY_CURRENT]?.numberOfItems}
                            maxCounter={MAX_NUMBER_OF_RESTING_EXCEED_PER_WEEK}
                            tooltipId={'intl-msg:timed.driving-time-table.numberOfTimes9HDailyDrivingTimeExceeding'}
                            activeColor={ActivityConfig[Activities.DRIVING].color}
                        />
                    </DrivingTimesCell>
                </div>
                <div
                    className={'display-flex flex-column border rounded'}
                    data-testId={getTestId(DrivingtimeViewCells.WEEKLY_DRIVING)}
                >
                    <DrivingTimesCell
                        testIdPrefix={getTestId(DrivingtimeViewCells.WEEKLY_DRIVING)}
                        currentMinutes={displayItem[TableColumn.DRIVING_WEEKLY_CURRENT]?.duration}
                        remainingMinutes={displayItem[TableColumn.REMAINING_WEEK_DRIVING_REMAINING]?.duration}
                        maximumMinutes={MAXIMUM_WEEKLY_DRIVING_MINUTES}
                        headerMessageId={'intl-msg:timed.common.signalHeader.week'}
                        textColor={'status-driving'}
                        progressBarColor={'progress-bar-status-driving'}
                    />
                </div>
                <div
                    className={'display-flex flex-column border rounded'}
                    data-testid={getTestId(DrivingtimeViewCells.TWO_WEEKLY_DRIVING)}
                >
                    <DrivingTimesCell
                        testIdPrefix={getTestId(DrivingtimeViewCells.TWO_WEEKLY_DRIVING)}
                        currentMinutes={displayItem[TableColumn.DRIVING_TWO_WEEKLY_CURRENT]?.duration}
                        remainingMinutes={displayItem[TableColumn.REMAINING_DRIVING_TWO_WEEKLY_CURRENT]?.duration}
                        maximumMinutes={MAXIMUM_TWO_WEEKLY_DRIVING_MINUTES}
                        headerMessageId={'intl-msg:timed.common.signalHeader.doubleWeek'}
                        textColor={'status-driving'}
                        progressBarColor={'progress-bar-status-driving'}
                    />
                </div>
                <div
                    className={'display-flex flex-column border rounded'}
                    data-testid={getTestId(DrivingtimeViewCells.DAILY_REST)}
                >
                    <RestingTimesCell
                        testIdPrefix={getTestId(DrivingtimeViewCells.DAILY_REST)}
                        restingMinutes={displayItem[TableColumn.MINIMUM_DAILY_REST_MINIMUM]?.duration}
                        headerMessageId={'intl-msg:timed.common.signalHeader.dailyRest'}
                        textColor={'status-resting'}
                    >
                        <span className={'margin-right-5'} />
                        <ClockCounterComponent
                            counter={displayItem[TableColumn.MINIMUM_DAILY_REST_MINIMUM]?.numberOfItems}
                            maxCounter={MAX_NUMBER_OF_DAILY_REST_VIOLATIONS}
                            tooltipId={'intl-msg:timed.common.numberOfRemainingReducedRestPeriods'}
                            activeColor={ActivityConfig[Activities.RESTING].color}
                        />
                    </RestingTimesCell>
                </div>
                <div
                    className={'display-flex flex-column border rounded'}
                    data-testid={getTestId(DrivingtimeViewCells.WEEKLY_REST)}
                >
                    <RestingTimesCell
                        testIdPrefix={getTestId(DrivingtimeViewCells.WEEKLY_REST)}
                        restingMinutes={displayItem[TableColumn.WEEKLY_REST_MINIMUM]?.duration}
                        headerMessageId={'intl-msg:timed.common.signalHeader.weeklyRest'}
                        textColor={'status-resting'}
                    />
                </div>
            </div>
        </div>
    );
};
